import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Container, Button, Badge, Nav, Offcanvas, Spinner
} from "react-bootstrap";
import {
    FaSignOutAlt, FaBox, FaUsers, FaBars, FaUser, FaChevronLeft, FaEyeSlash, FaEye,
    FaChevronRight, FaNetworkWired, FaHistory, FaLock, FaChartBar, FaWpforms, FaThList, FaDollarSign, FaBell, FaFileAlt
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import ProductsList from "../Components/Dashboard/Products";
import Customers from "../Components/Dashboard/Customers";
import Users from "../Components/Dashboard/Users";
import Reports from "../Components/Dashboard/Reports";
import Collection from "../Components/Dashboard/Collection";
import Access from "../Components/Dashboard/Access";
import LoginActivity from "../Components/Dashboard/LogActivity";
import PassReset from "../Components/Dashboard/PassReset";
import Application from "../Components/Dashboard/Application";
import CollectionFee from "../Components/Dashboard/CollectionFee";
import Notification from "../Components/Dashboard/Notification";
import Template from "../Components/Dashboard/Templates"
import { getRoleBadge } from "../Components/Shared/GetRoleBadge";
import HolidayDisplay from "../Components/HomeDashboard/HolidayDisplay.jsx";
import GraphTrends from "../Components/HomeDashboard/GraphTrends.jsx";
import IdleTimer from "../Components/HomeDashboard/IdleTimer.jsx";
import Snowfall from 'react-snowfall';
import { motion, AnimatePresence } from 'framer-motion'

const DashboardPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [userName, setUserName] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userAccess, setUserAccess] = useState(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const hasFetched = useRef(false);
    const isLoggingOut = useRef(false);
    const [singaporeTime, setSingaporeTime] = useState(new Date());
    const eyeballs = useRef([]);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);

    const lookAtMouse = useCallback((x, y) => {
        eyeballs.current.forEach(eyeball => {
            const rect = eyeball.getBoundingClientRect();
            const dx = x - (rect.left + rect.width / 2);
            const dy = y - (rect.top + rect.height / 2);
            const dist = Math.min(6, Math.hypot(dx, dy) / 15);
            const angle = Math.atan2(dy, dx);
            eyeball.style.transform = `translate(${Math.cos(angle) * dist}px, ${Math.sin(angle) * dist}px)`;
        });
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setSingaporeTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const handleMouse = (e) => lookAtMouse(e.clientX, e.clientY);
        document.addEventListener('mousemove', handleMouse);
        return () => document.removeEventListener('mousemove', handleMouse);
    }, [lookAtMouse]);

    const formattedTime = singaporeTime.toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Singapore"
    });
    const formattedDate = singaporeTime.toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Singapore"
    });

    useEffect(() => {
        const fetchUser = async () => {
            if (hasFetched.current || isLoggingOut.current) return;
            hasFetched.current = true;

            try {
                const res = await axios.get("https://www.azzenterprises.com/api/admin/getuser", { withCredentials: true });
                if (!res.data || !res.data.user_name) {
                    toast.error("Please login to access the dashboard");
                    setIsAuthenticated(false);
                    return;
                }
                setUserName(res.data.user_name);
                setUserRole(res.data.user_role);
                setUserAccess(res.data.can_access);
                setIsAuthenticated(true);
                localStorage.setItem("user_name", res.data.user_name);
                localStorage.setItem("user_role", res.data.user_role);
                localStorage.setItem("can_access", res.data.can_access);
            } catch (error) {
                console.error("Fetch user error:", error);
                toast.error("Please login to access the dashboard");
                setIsAuthenticated(false);
            }
        };

        const cachedUser = localStorage.getItem("user_name");
        const cachedRole = localStorage.getItem("user_role");
        const cachedAccess = localStorage.getItem("can_access");

        if (cachedUser && cachedRole) {
            setUserName(cachedUser);
            setUserRole(cachedRole);
            setUserAccess(cachedAccess);
            setIsAuthenticated(true);
        } else {
            fetchUser();
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated === false && !isLoggingOut.current) {
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_role");
            localStorage.removeItem("can_access");
            hasFetched.current = false;

            navigate("/login", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const ROLE_CONFIG = {
        developer: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/user", label: "Users", icon: <FaUser /> },
            { path: "/access", label: "Access", icon: <FaNetworkWired /> },
            { path: "/activity", label: "Activity", icon: <FaHistory /> },
            { path: "/reset-password", label: "Password", icon: <FaLock /> },
            { path: "/collection", label: "Collection", icon: <FaThList /> },
            { path: "/reports", label: "Reports", icon: <FaChartBar /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> },
            { path: "/processing-fee", label: "Proc Fee", icon: <FaDollarSign /> },
            { path: "/notification", label: "Notifications", icon: <FaBell /> },
            { path: "/templates", label: "Templates", icon: <FaFileAlt /> },
        ],
        superadmin: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/user", label: "Users", icon: <FaUser /> },
            { path: "/access", label: "Access", icon: <FaNetworkWired /> },
            { path: "/activity", label: "Activity", icon: <FaHistory /> },
            { path: "/reset-password", label: "Password", icon: <FaLock /> },
            { path: "/collection", label: "Collection", icon: <FaThList /> },
            { path: "/reports", label: "Reports", icon: <FaChartBar /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> },
            { path: "/processing-fee", label: "Proc Fee", icon: <FaDollarSign /> },
            // { path: "/notification", label: "Notifications", icon: <FaBell /> },
            // { path: "/templates", label: "Templates", icon: <FaFileAlt /> },
        ],
        supervisor: [
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ],
        viewer: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ],
        editor: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ]
    };

    const navItems = [
        { path: "/customers", label: "Customers", icon: <FaUsers /> },
        ...(ROLE_CONFIG[userRole] || []),
    ];

    const handleLogout = async () => {
        if (isLoggingOut.current) return;

        isLoggingOut.current = true;
        try {
            await axios.post("https://www.azzenterprises.com/api/admin/logout", {}, {
                withCredentials: true
            });
        } catch (error) {
            console.error("Logout API error:", error);
        } finally {
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_role");
            localStorage.removeItem("can_access");

            setUserName(null);
            setUserRole(null);
            setUserAccess(null);
            setIsAuthenticated(false);
            hasFetched.current = false;

            toast.success("Logged out successfully");

            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 1000);
        }
    };

    const pageTitles = {
        "/customers": "Customers",
        "/products-list": "Products",
        "/user": "Role-Based Access (RBAC)",
        "/access": "Customer Privileges",
        "/activity": "Staff Activity Log",
        "/reset-password": "Change Password",
        "/collection": "Collection Management - Overdue",
        "/reports": "EMI Payment & Collection Reports",
        "/application": "Application",
        "/processing-fee": "Processing Fee Management",
        "/notification": "Notification Center",
        "/templates": "Template Manager",
    };

    const getPageSubtitle = (pathname) => {
        const subtitles = {
            "/customers": "Customer Management Dashboard",
            "/products-list": "EMI Products Catalog",
            "/user": "Role & Permission Settings",
            "/access": "Customer Access Control",
            "/activity": "Staff Activity Monitor",
            "/reset-password": "Security Settings",
            "/collection": "Overdue Collections Dashboard",
            "/reports": "EMI Analytics & Reports",
            "/application": "Application Dashboard",
            "/processing-fee": "Fee Management System",
            "/notification": "Manage and track all system communications",
            "/templates": "Design and manage notification templates",
            "/": "Main Dashboard"
        };

        return subtitles[pathname] || "EMI Analytics Dashboard";
    };


    const renderContent = () => {
        if (isAuthenticated === null) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            );
        }

        if (!isAuthenticated) {
            return null;
        }

        switch (location.pathname) {
            case "/customers": return <Customers />;
            case "/products-list": return <ProductsList />;
            case "/user": return <Users />;
            case "/access": return <Access />;
            case "/activity": return <LoginActivity />;
            case "/reset-password": return <PassReset />;
            case "/collection": return <Collection />;
            case "/reports": return <Reports />;
            case "/application": return <Application />;
            case "/processing-fee": return <CollectionFee />;
            case "/notification": return <Notification />;
            case "/templates": return <Template />
            default:
                return (
                    <div className="p-4 text-center">
                        <h4 className="fw-bold mb-3">Welcome to Your Dashboard</h4>
                        <p className="text-muted">Here's what's happening with your store today.</p>
                        <Button variant="outline-primary" onClick={() => navigate("/products-list")}>
                            Explore Products
                        </Button>
                    </div>
                );
        }
    };

    if (isAuthenticated === null) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={1000} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </>
        );
    }

    if (!isAuthenticated) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={1000} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Redirecting to login...</span>
                    </Spinner>
                </div>
            </>
        );
    }

    const getMessage = (index) => {
        const messages = [
            <small key="1" className="badge bg-danger-subtle text-danger-emphasis px-3 py-1 me-5">
                ⚠️ Any product delivered without confirmed eGIRO creation will be the joint responsibility of the Sales and Accounts teams.
            </small>,

            <small key="2" className="badge bg-warning-subtle text-warning-emphasis px-3 py-1 me-5">
                📢 Sales and Accounts must verify eGIRO status and customer consent before approving product delivery.
            </small>,

            <small key="3" className="badge bg-info-subtle text-info-emphasis px-3 py-1 me-5">
                ⚠️ Failed or pending eGIRO attempts must be retried, recorded, and escalated by Sales or Accounts within 24 hours.
            </small>,

            <small key="4" className="badge bg-success-subtle text-success-emphasis px-3 py-1 me-5">
                ✅ Ensure accurate bank details and explicit customer consent to prevent eGIRO processing delays.
            </small>,

            <small key="5" className="badge bg-primary-subtle text-primary-emphasis px-3 py-1 me-5">
                ⏰ If eGIRO status is unavailable, Sales and Accounts must recheck bank systems and retry after 30 minutes.
            </small>,

            <small key="6" className="badge bg-danger-subtle text-danger-emphasis px-3 py-1 me-5">
                🚫 Product delivery must not proceed unless eGIRO status is marked as “Successful” or formally approved.
            </small>,

            <small key="7" className="badge bg-warning-subtle text-warning-emphasis px-3 py-1 me-5">
                📄 All eGIRO-related exceptions must be documented with reason and timestamp by Sales or Accounts.
            </small>,

            <small key="8" className="badge bg-info-subtle text-info-emphasis px-3 py-1 me-5">
                🔄 Reattempt eGIRO setup immediately if bank validation fails or customer details are incomplete.
            </small>,

            <small key="9" className="badge bg-success-subtle text-success-emphasis px-3 py-1 me-5">
                🤝 Sales and Accounts must coordinate closely to ensure smooth and compliant eGIRO onboarding.
            </small>,

            <small key="10" className="badge bg-primary-subtle text-primary-emphasis px-3 py-1 me-5">
                🛡️ Proper eGIRO compliance protects both the customer experience and internal accountability.
            </small>
        ];

        return messages[index % messages.length];
    };

    const messages = [
        getMessage(0),
        getMessage(1),
        getMessage(2),
        getMessage(3),
        getMessage(4),
    ];

    return (
        <Container fluid className="bg-light min-vh-100 p-0 d-flex">
            <div className="d-md-none shadow-sm bg-light p-2 sticky-top w-100" style={{ zIndex: 1030 }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Button variant="light" className="me-2" onClick={() => setShowMobileMenu(true)}>
                            <FaBars />
                        </Button>
                        <span className="fw-semibold">Welcome,</span>
                        <Badge bg="light" text="dark" className="ms-2">{userName}</Badge>
                    </div>
                    <Button onClick={handleLogout} variant="outline-danger" size="sm" className="d-flex align-items-center gap-2">
                        <FaSignOutAlt />
                        <span className="d-none d-md-inline">Logout</span>
                    </Button>
                </div>
            </div>

            <motion.div
                className="d-none d-md-flex flex-column shadow-sm sidebar-sticky"
                style={{
                    height: "100vh",
                    position: "sticky",
                    top: 0,
                    overflow: "hidden",
                    zIndex: 1020,
                    flexShrink: 0,
                    background: "#ffffff",
                    color: "#2d3748"
                }}
                animate={{
                    width: isSidebarCollapsed ? "50px" : "20%",
                    minWidth: isSidebarCollapsed ? "50px" : "150px",
                    maxWidth: isSidebarCollapsed ? "50px" : "150px"
                }}
                transition={{
                    duration: 0.3,
                    ease: [0.25, 0.46, 0.45, 0.94]
                }}
                layout
            >
                <motion.div
                    className={`d-flex ${isSidebarCollapsed ? 'flex-column-reverse py-3' : 'flex-row py-3'} justify-content-center align-items-center gap-2`}
                    layout
                >
                    <motion.div
                        className="shadow-none hover-lift rounded-circle"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Button
                            variant="light"
                            size="sm"
                            onClick={() => setIsHeaderVisible(!isHeaderVisible)}
                            style={{
                                width: "32px",
                                height: "32px",
                                background: "#f7fafc",
                                border: "1px solid #e2e8f0",
                                color: "#4a5568",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                            title={isHeaderVisible ? "Hide Header" : "Show Header"}
                        >
                            {isHeaderVisible ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                        </Button>
                    </motion.div>

                    <motion.div
                        className="shadow-none hover-lift rounded-circle"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Button
                            variant="light"
                            size="sm"
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            style={{
                                width: "32px",
                                height: "32px",
                                background: "#f7fafc",
                                border: "1px solid #e2e8f0",
                                color: "#4a5568",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            {isSidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                        </Button>
                    </motion.div>
                </motion.div>

                <IdleTimer
                    userName={userName}
                    isSidebarCollapsed={isSidebarCollapsed}
                    onLogout={handleLogout}
                />

                <Nav className="flex-column flex-grow-1 px-1 py-2">
                    {navItems.map((item) => (
                        <Nav.Link
                            key={item.path}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(item.path);
                            }}
                            className={`nav-item py-1.5 px-3 rounded-2 mb-1 d-flex align-items-center transition-all fs-2xs ${location.pathname === item.path ? "shadow-sm active-nav" : "hover-nav"}`}
                            style={{
                                cursor: "pointer",
                                background: location.pathname === item.path ? "#4c51bf" : "transparent",
                                border: location.pathname === item.path ? "1px solid #4c51bf" : "1px solid transparent",
                                color: location.pathname === item.path ? "#ffffff" : "#4a5568",
                                minHeight: "40px"
                            }}
                            as={motion.div}
                            whileHover={{ x: 4 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <span className="me-2 fs-1xs d-flex align-items-center justify-content-center" style={{
                                width: "20px",
                                opacity: location.pathname === item.path ? 1 : 0.8,
                                flexShrink: 0,
                                color: "inherit"
                            }}>
                                {item.icon}
                            </span>

                            <AnimatePresence>
                                {!isSidebarCollapsed && (
                                    <motion.span
                                        className="fw-medium"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -10 }}
                                        transition={{ duration: 0.15 }}
                                    >
                                        {item.label}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </Nav.Link>
                    ))}
                </Nav>

                <AnimatePresence>
                    {!isSidebarCollapsed && (
                        <motion.div
                            className="p-2 border-top mt-auto"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            style={{
                                background: "#f8f9ff",
                                borderTop: "1px solid #e2e8f0"
                            }}
                        >
                            <Button
                                onClick={handleLogout}
                                variant="outline-danger"
                                size="sm"
                                className="w-100 rounded-2 py-1 px-2 fs-2xs d-flex align-items-center justify-content-center gap-1 transition-all"
                                style={{
                                    background: "linear-gradient(135deg, #fed7d7 0%, #fc8181 100%)",
                                    border: "1px solid #fc8181",
                                    color: "#742a2a",
                                    position: "relative",
                                    overflow: "hidden"
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    backgroundColor: "#dc3545",
                                    borderColor: "#dc3545",
                                    color: "white",
                                    boxShadow: "0 4px 15px rgba(220, 53, 69, 0.4)",
                                    transition: {
                                        duration: 0.3,
                                        backgroundColor: { duration: 0.2 }
                                    }
                                }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <motion.div
                                    animate={{
                                        x: [0, 3, -3, 0],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        repeatDelay: 5
                                    }}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <FaSignOutAlt className="fs-1xs me-1" />
                                    <span>Logout</span>
                                </motion.div>

                                <motion.div
                                    className="position-absolute top-0 start-0 w-100 h-100"
                                    initial={{ x: "-100%" }}
                                    whileHover={{ x: "100%" }}
                                    transition={{ duration: 0.5 }}
                                    style={{
                                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                                    }}
                                />

                                <motion.div
                                    className="position-absolute bottom-0 start-0 w-100"
                                    initial={{ scaleX: 0 }}
                                    whileHover={{ scaleX: 1 }}
                                    transition={{ duration: 0.3 }}
                                    style={{
                                        height: "2px",
                                        background: "white",
                                        transformOrigin: "left",
                                    }}
                                />
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <div className="flex-grow-1 p-3 position-relative overflow-hidden" style={{
                width: isSidebarCollapsed ? "calc(100% - 50px)" : "80%",
                minHeight: "100vh",
                background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)'
            }}>
                <AnimatePresence>
                    {isHeaderVisible && (
                        <motion.div
                            initial={{ opacity: 0, y: -30 }}
                            animate={{
                                opacity: isHeaderVisible ? 1 : 0,
                                y: isHeaderVisible ? 0 : -30,
                                height: isHeaderVisible ? "auto" : 0
                            }}
                            exit={{ opacity: 0, y: -20, height: 0 }}
                            transition={{
                                duration: 0.4,
                                ease: "easeInOut",
                                height: { duration: 0.3, ease: "easeInOut" }
                            }}
                            style={{ overflow: "hidden" }}
                        >
                            <div className="glass-header-soft position-relative overflow-hidden snow-container">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 p-md-4 gap-3">
                                    <div>
                                        <h5 className="fw-bold mb-0 text-dark">
                                            {pageTitles[location.pathname] || "Dashboard"}
                                        </h5>
                                        <small className="fw-semibold fst-italic" style={{ color: '#4c51bf' }}>
                                            {getPageSubtitle(location.pathname)}
                                        </small>
                                    </div>

                                    <div className="text-end position-relative">
                                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                            <div className="flex-grow-1 text-center d-none d-md-block">
                                                <GraphTrends />
                                            </div>
                                            <div className="flex-grow-1 text-center d-none d-md-block">
                                                <HolidayDisplay />
                                            </div>
                                            <div className="glass-info-soft rounded-2 p-2 border border-light shadow-sm position-relative z-10 d-flex flex-column align-items-center">
                                                <div className="text-muted-50 xsmall mb-1 fw-medium" style={{ fontSize: '0.65rem' }}>{formattedDate}</div>
                                                <div className="fw-semibold text-dark" style={{ fontSize: '0.85rem' }}>{formattedTime}</div>
                                                <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                                    {getRoleBadge(userRole)}
                                                    <motion.div
                                                        className="rounded-circle position-relative"
                                                        style={{
                                                            width: '6px',
                                                            height: '6px',
                                                            backgroundColor: '#10b981',
                                                            boxShadow: '0 0 8px rgba(16, 185, 129, 0.6)'
                                                        }}
                                                        animate={{
                                                            scale: [1, 1.3, 1],
                                                            boxShadow: [
                                                                '0 0 8px rgba(16, 185, 129, 0.6)',
                                                                '0 0 12px rgba(16, 185, 129, 0.8)',
                                                                '0 0 8px rgba(16, 185, 129, 0.6)'
                                                            ]
                                                        }}
                                                        transition={{
                                                            duration: 1.5,
                                                            repeat: Infinity,
                                                            ease: "easeInOut"
                                                        }}
                                                    >
                                                        <motion.div
                                                            className="position-absolute rounded-circle"
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                border: '2px solid rgba(16, 185, 129, 0.4)',
                                                                top: '-1px',
                                                                left: '-1px'
                                                            }}
                                                            animate={{
                                                                scale: [1, 1.4, 1],
                                                                opacity: [0.4, 0.1, 0.4]
                                                            }}
                                                            transition={{
                                                                duration: 2,
                                                                repeat: Infinity,
                                                                ease: "easeOut",
                                                                delay: 0.5
                                                            }}
                                                        />
                                                    </motion.div>
                                                    <span className="text-success fw-semibold" style={{ fontSize: '0.5rem' }}>Online</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="snow-layers relative">
                                    <Snowfall color="#e6e9ff" snowflakeCount={30} speed={[0.05, 0.2]} radius={[0.5, 2]} wind={[-0.1, 0.1]} rotationSpeed={[-0.5, 0.5]} />
                                    <Snowfall color="#d1d5ff" snowflakeCount={20} speed={[0.1, 0.3]} radius={[1, 3]} wind={[-0.2, 0.2]} rotationSpeed={[-1, 1]} />
                                    <Snowfall color="#a5b4fc" snowflakeCount={8} speed={[0.15, 0.4]} radius={[1.5, 4]} wind={[-0.3, 0.3]} rotationSpeed={[-1.5, 1.5]} />
                                </div>
                            </div>

                            <div
                                style={{
                                    position: "absolute",
                                    top: "15px",
                                    left: 0,
                                    right: 0,
                                    height: "30px",
                                    width: "100vw",
                                    fontSize: '13px'
                                }}
                            >
                                <motion.div
                                    className="d-flex align-items-center"
                                    style={{ whiteSpace: "nowrap" }}
                                    animate={{ x: ["0%", "-50%"] }}
                                    transition={{
                                        duration: 60,
                                        ease: "linear",
                                        repeat: Infinity,
                                    }}
                                >
                                    {messages.map((msg, i) => (
                                        <div key={`a-${i}`} style={{ marginRight: "50px" }}>
                                            {msg}
                                        </div>
                                    ))}
                                    {messages.map((msg, i) => (
                                        <div key={`b-${i}`} style={{ marginRight: "50px" }}>
                                            {msg}
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="content-card-soft bg-white/90 rounded-3 shadow-md border border-light overflow-hidden">
                    <div className="p-2 bg-white">{renderContent()}</div>
                </div>
            </div>

            <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} placement="start" className="w-75">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold">Dashboard</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    <Nav className="flex-column">
                        {navItems.map((item) => (
                            <Nav.Link
                                key={item.path}
                                onClick={() => {
                                    navigate(item.path);
                                    setShowMobileMenu(false);
                                }}
                                className={`px-4 py-3 d-flex align-items-center border-bottom ${location.pathname === item.path ? "bg-primary text-white" : "text-dark"
                                    }`}
                            >
                                <span className="me-3">{item.icon}</span>
                                {item.label}
                            </Nav.Link>
                        ))}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </Container>
    );
};

export default DashboardPage;